/**
 * ChorusModule — a single-tap modulated delay. LFO sweeps the delay
 * time around a short base (10ms) to produce the classic pitch-smearing
 * shimmer.
 *
 * Graph:
 *
 *   input ─┬─ dryGain ─────────────────────── outputMix ─►
 *          └─► delay ─► wetGain ───────────── outputMix
 *   lfo ─► lfoGain ─► delay.delayTime   (AudioParam sum)
 *
 * No feedback on chorus — feedback loops push this into flanger
 * territory, which has its own module. Depth scales the LFO output,
 * so "depth = 0" means the delay is a static 10ms tap (barely
 * audible on its own; users generally keep mix near zero at that
 * point).
 */

import type { AudioEngine } from '../AudioEngine';
import { NumericParameter, type AnyParameter } from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';

/** Static middle of the delay sweep, in seconds (classic chorus territory). */
const BASE_DELAY_SEC = 0.01;
/** Max LFO swing around the base — ±this × depth, in seconds. */
const MAX_SWING_SEC = 0.005;

export class ChorusModule implements SynthModule {
  readonly kind = 'signal' as const;
  readonly type = 'chorus' as const;
  readonly displayName = 'CHORUS';
  readonly id: string;
  bypass = false;

  readonly rate = new NumericParameter({
    name: 'Rate',
    default: 0.6,
    min: 0.05,
    max: 8,
    curve: 'exp',
    unit: 'Hz',
    smoothingMs: 20,
    formatter: (v) => (v < 1 ? v.toFixed(2) : v.toFixed(1)),
  });
  readonly depth = new NumericParameter({
    name: 'Depth',
    default: 0.5,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 20,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });
  readonly mix = new NumericParameter({
    name: 'Mix',
    default: 0.4,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 20,
    randomSkew: 0.7,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });

  readonly parameters: readonly AnyParameter[] = [this.rate, this.depth, this.mix];

  private engine: AudioEngine | null = null;
  private inputNode: GainNode | null = null;
  private outputMixNode: GainNode | null = null;
  private delayNode: DelayNode | null = null;
  private dryGain: GainNode | null = null;
  private wetGain: GainNode | null = null;
  private lfoOsc: OscillatorNode | null = null;
  private lfoGain: GainNode | null = null;
  private started = false;
  private readonly unsubs: Array<() => void> = [];

  constructor(id?: string) {
    this.id = id ?? mintId('chorus');
  }

  get input(): AudioNode | null {
    return this.inputNode;
  }
  get output(): AudioNode | null {
    return this.outputMixNode;
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    this.engine = engine;
    const ctx = engine.context;

    this.inputNode = ctx.createGain();
    this.outputMixNode = ctx.createGain();
    // 50ms max delay is plenty for chorus-range sweeps.
    this.delayNode = ctx.createDelay(0.05);
    this.delayNode.delayTime.value = BASE_DELAY_SEC;
    this.dryGain = ctx.createGain();
    this.wetGain = ctx.createGain();

    this.inputNode.connect(this.dryGain).connect(this.outputMixNode);
    this.inputNode.connect(this.delayNode).connect(this.wetGain).connect(this.outputMixNode);

    this.lfoOsc = ctx.createOscillator();
    this.lfoOsc.type = 'sine';
    this.lfoOsc.frequency.value = this.rate.value;
    this.lfoGain = ctx.createGain();
    this.lfoGain.gain.value = this.depth.value * MAX_SWING_SEC;
    this.lfoOsc.connect(this.lfoGain).connect(this.delayNode.delayTime);
    this.lfoOsc.start();

    this.rate.bindAudioParam(this.lfoOsc.frequency, ctx);
    // Depth writes into lfoGain.gain scaled by MAX_SWING_SEC — can't use
    // bindAudioParam directly because of the scale. Manual subscribe.
    this.unsubs.push(
      this.depth.subscribe((v) => {
        if (!this.lfoGain || !this.engine) return;
        const ctx2 = this.engine.context;
        const t = ctx2.currentTime;
        this.lfoGain.gain.cancelScheduledValues(t);
        this.lfoGain.gain.setTargetAtTime(v * MAX_SWING_SEC, t, 0.02);
      }),
    );

    this.applyMix(this.mix.value, true);
    this.unsubs.push(this.mix.subscribe((v) => this.applyMix(v)));

    this.started = true;
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    try {
      this.lfoOsc?.stop();
    } catch {
      /* already stopped */
    }
    this.started = false;
  }

  getAudioParam(paramName: string): AudioParam | null {
    if (paramName === 'Rate') return this.lfoOsc?.frequency ?? null;
    return null;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    readParamState(this.parameters, state.params, 'preset');
  }

  private applyMix(mix: number, immediate = false): void {
    if (!this.dryGain || !this.wetGain || !this.engine) return;
    const ctx = this.engine.context;
    const t = ctx.currentTime;
    const dry = 1 - mix;
    const wet = mix;
    if (immediate) {
      this.dryGain.gain.setValueAtTime(dry, t);
      this.wetGain.gain.setValueAtTime(wet, t);
    } else {
      this.dryGain.gain.cancelScheduledValues(t);
      this.dryGain.gain.setTargetAtTime(dry, t, 0.01);
      this.wetGain.gain.cancelScheduledValues(t);
      this.wetGain.gain.setTargetAtTime(wet, t, 0.01);
    }
  }
}
